import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase/client';

interface AuthContextType {
  user: any | null; // Mapped user object
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  sendEmailLogin: (email: string) => Promise<{ error: any }>;
  verifyEmailCode: (email: string, code: string) => Promise<{ error: any }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Maps Supabase user properties to Firebase-like signatures for compatibility.
 */
const mapSupabaseUser = (supabaseUser: any) => {
  if (!supabaseUser) return null;
  return {
    ...supabaseUser,
    uid: supabaseUser.id, // Crucial compatibility mapping
    displayName: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || '',
    photoURL: supabaseUser.user_metadata?.avatar_url || '',
    email: supabaseUser.email
  };
};

/**
 * Ensures user has a record in public.user_profiles table.
 */
const ensureUserDoc = async (u: any) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', u.id)
      .maybeSingle();

    if (!data && !error) {
      console.log('[Auth] Creating new profile in Supabase for user:', u.id);
      // Profile trigger handle_new_user should automatically create this, 
      // but we do a safe insert check just in case.
      await supabase.from('user_profiles').insert({
        user_id: u.id,
        email: u.email || '',
        credits: 5,
        subscription_status: 'none'
      });
      
      // Log welcome transaction
      await supabase.from('credit_transactions').insert({
        user_id: u.id,
        type: 'bonus',
        amount: 5,
        description: 'Welcome bonus credits',
        related_action: 'user_registration'
      });
    }

    // Check and link active guest session if present
    try {
      const { getGuestSession, linkGuestSessionToUser } = await import('../utils/guestSession');
      const guestSession = getGuestSession();
      if (guestSession && guestSession.subscriptionStatus === 'active') {
        console.log('[Auth] Active guest session found, linking to:', u.id);
        await linkGuestSessionToUser(u.id, guestSession.id);
      }
    } catch (guestErr) {
      console.warn('[Auth] Guest session linking failed (non-blocking):', guestErr);
    }
  } catch (err) {
    console.error('[Auth] Error ensuring user profile in Supabase:', err);
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const mapped = mapSupabaseUser(session.user);
        setUser(mapped);
        ensureUserDoc(mapped);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    // Listen for auth changes
    // IMPORTANT: this callback must stay synchronous and must not await other Supabase calls.
    // supabase-js runs it while holding its auth lock; awaiting a query here (ensureUserDoc)
    // deadlocks session restore on page reload, which made users look signed out.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: any, session: any) => {
      console.log('[Auth] Event state change:', event);
      if (session?.user) {
        const mapped = mapSupabaseUser(session.user);
        setUser(mapped);
        if (event === 'SIGNED_IN') {
          setTimeout(() => { ensureUserDoc(mapped); }, 0);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      if (data.user) {
        const mapped = mapSupabaseUser(data.user);
        setUser(mapped);
        await ensureUserDoc(mapped);
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.user) {
        const mapped = mapSupabaseUser(data.user);
        setUser(mapped);
        await ensureUserDoc(mapped);
      }
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  // Passwordless sign-in: emails a one-click link (and a 6-digit code if the
  // Supabase email template includes {{ .Token }}). Creates the account if needed.
  const sendEmailLogin = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
          shouldCreateUser: true
        }
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const verifyEmailCode = async (email: string, code: string) => {
    try {
      const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const logout = async () => {
    localStorage.removeItem('subscriptionStatus');
    await supabase.auth.signOut();
    setUser(null);
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login?reset=success`
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    sendEmailLogin,
    verifyEmailCode,
    logout,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};